import {
	GQLImageFragment,
	GQLMerchDecorationFragment,
	GQLMerchDecorationTypeType,
	GQLOperationalSettingsFragment,
	GQLOrderItemPrintDecorationType,
	GQLOrderItemPrintFragment,
	GQLProductSideAvailableDecorationType,
	GQLProductTileFragment,
} from '@theprintbar/graphql';
import { ArtworkAndMeta, Decoration, DecorationServer } from '.';
import { getBlobUrl, isDefined } from '../../utils';

export function getDecorationName(
	decorationType: GQLProductSideAvailableDecorationType | GQLMerchDecorationTypeType | GQLOrderItemPrintDecorationType,
) {
	switch (decorationType) {
		case GQLOrderItemPrintDecorationType.Dtf:
		case GQLOrderItemPrintDecorationType.Dtf:
		case GQLMerchDecorationTypeType.Dtf:
			return 'Direct to Film';
		case GQLProductSideAvailableDecorationType.Emb:
		case GQLMerchDecorationTypeType.Emb:
		case GQLMerchDecorationTypeType.Emb:
			return 'Embroidery';
		case GQLProductSideAvailableDecorationType.Scr:
		case GQLMerchDecorationTypeType.Scr:
		case GQLMerchDecorationTypeType.Scr:
			return 'Screen Printing';
		case GQLProductSideAvailableDecorationType.Dtg:
		case GQLOrderItemPrintDecorationType.Dtg:
		case GQLMerchDecorationTypeType.Dtg:
		default:
			return 'Digital Printing';
	}
}

export const decorationHasArtwork = (decoration?: Decoration | DecorationServer | GQLMerchDecorationFragment | GQLOrderItemPrintFragment) =>
	!!decoration &&
	(isOrderItemPrint(decoration)
		? !!decoration.artwork?.filesize
		: isMerchDecoration(decoration)
		? !!decoration.design
		: !!decoration.artwork?.size);

export const isDTGDec = (
	decoration?: Decoration | DecorationServer | GQLMerchDecorationFragment | GQLOrderItemPrintFragment,
	ensureArtwork?: boolean,
) =>
	decoration &&
	(!ensureArtwork || decorationHasArtwork(decoration)) &&
	(isOrderItemPrint(decoration)
		? decoration.decoration === GQLOrderItemPrintDecorationType.Dtg
		: decoration?.type === GQLProductSideAvailableDecorationType.Dtg || decoration?.type === GQLMerchDecorationTypeType.Dtg);
export const isDTFDec = (
	decoration?: Decoration | DecorationServer | GQLMerchDecorationFragment | GQLOrderItemPrintFragment,
	ensureArtwork?: boolean,
) =>
	decoration &&
	(!ensureArtwork || decorationHasArtwork(decoration)) &&
	(isOrderItemPrint(decoration)
		? decoration.decoration === GQLOrderItemPrintDecorationType.Dtf
		: decoration?.type === GQLProductSideAvailableDecorationType.Dtf || decoration?.type === GQLMerchDecorationTypeType.Dtf);
export const isSCRDec = (
	decoration?: Decoration | DecorationServer | GQLMerchDecorationFragment | GQLOrderItemPrintFragment,
	ensureArtwork?: boolean,
) =>
	decoration &&
	(!ensureArtwork || decorationHasArtwork(decoration)) &&
	(isOrderItemPrint(decoration)
		? decoration.decoration === GQLOrderItemPrintDecorationType.Scr
		: decoration?.type === GQLProductSideAvailableDecorationType.Scr || decoration?.type === GQLMerchDecorationTypeType.Scr);
export const isEMBDec = (
	decoration?: Decoration | DecorationServer | GQLMerchDecorationFragment | GQLOrderItemPrintFragment,
	ensureArtwork?: boolean,
) =>
	decoration &&
	(!ensureArtwork || decorationHasArtwork(decoration)) &&
	(isOrderItemPrint(decoration)
		? decoration.decoration === GQLOrderItemPrintDecorationType.Emb
		: decoration?.type === GQLProductSideAvailableDecorationType.Emb || decoration?.type === GQLMerchDecorationTypeType.Emb);

export const isMerchDecoration = (
	decoration: GQLOrderItemPrintFragment | GQLMerchDecorationFragment | Decoration | DecorationServer,
): decoration is GQLMerchDecorationFragment => {
	return !!(decoration as GQLMerchDecorationFragment).id && !!(decoration as GQLMerchDecorationFragment).design;
};

export const isOrderItemPrint = (
	decoration: GQLOrderItemPrintFragment | GQLMerchDecorationFragment | Decoration | DecorationServer,
): decoration is GQLOrderItemPrintFragment => {
	return !!(
		(decoration as GQLOrderItemPrintFragment).id &&
		!!(decoration as GQLOrderItemPrintFragment).artwork &&
		!!(decoration as GQLOrderItemPrintFragment).decoration
	);
};

export function getMaxDecorationTypeColours(type?: GQLProductSideAvailableDecorationType, product?: GQLProductTileFragment) {
	if (!!product?.customDecorationMaxColours) {
		return product.customDecorationMaxColours;
	}

	if (!type || type === GQLProductSideAvailableDecorationType.Dtg || type === GQLProductSideAvailableDecorationType.Dtf) return 0;

	return type === GQLProductSideAvailableDecorationType.Emb ? 8 : 6;
}

export function getMaxDecorationColours(decoration?: Decoration, product?: GQLProductTileFragment) {
	if (!!product?.customDecorationMaxColours) {
		return product.customDecorationMaxColours;
	}

	if (!decoration || isDTGDec(decoration) || isDTFDec(decoration)) return 0;

	return isEMBDec(decoration) ? 8 : 6;
}

export function getDecorationMeta(decoration: GQLOrderItemPrintFragment | GQLMerchDecorationFragment | Decoration | DecorationServer) {
	const { width, height, left, top, stitches, requestedColours } =
		isMerchDecoration(decoration) || isOrderItemPrint(decoration) ? decoration : decoration.meta;
	return {
		width,
		height,
		left,
		top,
		stitches,
		requestedColours,
		colours:
			(isMerchDecoration(decoration) || isOrderItemPrint(decoration)
				? decoration.colours.structure?.filter(isDefined) || []
				: decoration.meta.colours) || [],
	};
}

export function getDecorationImageUrl(decoration: ArtworkAndMeta) {
	if (!decoration.artwork || !decoration.artwork.size) return '';
	return getBlobUrl(decoration.meta.trueview || decoration.artwork);
}

export function getDecorationBGColour(
	type: GQLProductSideAvailableDecorationType | GQLMerchDecorationTypeType | GQLOrderItemPrintDecorationType,
	product?: Pick<GQLProductTileFragment, 'customDecorationName'> | null,
) {
	if (product?.customDecorationName) return 'bg-blue-bright';

	switch (type) {
		case GQLOrderItemPrintDecorationType.Dtg:
		case GQLMerchDecorationTypeType.Dtg:
		case GQLProductSideAvailableDecorationType.Dtg:
			return 'bg-blue-bright';
		case GQLOrderItemPrintDecorationType.Scr:
		case GQLMerchDecorationTypeType.Scr:
		case GQLProductSideAvailableDecorationType.Scr:
			return 'bg-pink';
		case GQLOrderItemPrintDecorationType.Emb:
		case GQLMerchDecorationTypeType.Emb:
		case GQLProductSideAvailableDecorationType.Emb:
			return 'bg-orange';
		case GQLOrderItemPrintDecorationType.Dtf:
		case GQLMerchDecorationTypeType.Dtf:
		case GQLProductSideAvailableDecorationType.Dtf:
			return 'bg-yellow';
		default:
			return 'bg-purple';
	}
}

export function getDecorationContent(
	type: GQLProductSideAvailableDecorationType | GQLMerchDecorationTypeType | GQLOrderItemPrintDecorationType,
	operationalSettings: GQLOperationalSettingsFragment,
	product?: Pick<GQLProductTileFragment, 'customDecorationName' | 'customDecorationDescription' | 'customDecorationIcon'> | null,
) {
	let title = '';
	let text = '';
	let artworkRequirements: string | undefined;
	let icon: { altText?: string | null; image: GQLImageFragment } | null | undefined;
	let bgColour: string | undefined;

	if (product?.customDecorationName) {
		return {
			title: product.customDecorationName,
			icon: product.customDecorationIcon || operationalSettings.dtgIcon,
			text: product.customDecorationDescription || '',
		};
	}

	switch (type) {
		case GQLOrderItemPrintDecorationType.Dtg:
		case GQLMerchDecorationTypeType.Dtg:
		case GQLProductSideAvailableDecorationType.Dtg:
			title = operationalSettings.dtgTitle;
			text = operationalSettings.dtgShortDescription;
			icon = operationalSettings.dtgIcon;
			artworkRequirements = operationalSettings.dtgArtworkRequirements;
			break;
		case GQLOrderItemPrintDecorationType.Scr:
		case GQLMerchDecorationTypeType.Scr:
		case GQLProductSideAvailableDecorationType.Scr:
			title = operationalSettings.scrTitle;
			text = operationalSettings.scrShortDescription;
			icon = operationalSettings.scrIcon;
			artworkRequirements = operationalSettings.scrArtworkRequirements;
			break;
		case GQLOrderItemPrintDecorationType.Emb:
		case GQLMerchDecorationTypeType.Emb:
		case GQLProductSideAvailableDecorationType.Emb:
			title = operationalSettings.embTitle;
			text = operationalSettings.embShortDescription;
			icon = operationalSettings.embIcon;
			artworkRequirements = operationalSettings.embArtworkRequirements;
			break;
		case GQLOrderItemPrintDecorationType.Dtf:
		case GQLMerchDecorationTypeType.Dtf:
		case GQLProductSideAvailableDecorationType.Dtf:
			title = operationalSettings.dtfTitle;
			text = operationalSettings.dtfShortDescription;
			icon = operationalSettings.dtfIcon;
			artworkRequirements = operationalSettings.dtfArtworkRequirements;
			break;
	}

	bgColour = getDecorationBGColour(type, product);

	return {
		title,
		text,
		icon,
		artworkRequirements,
		bgColour,
	};
}
